import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { map, Observable, of, switchMap } from 'rxjs';
import { API_URL } from '@core/constants';
import { DocumentExt, UploadDocument, IDocumentResponseDTO } from '@core/model';
import { ApplicationStateService } from './application-state.service';
import { UploadFile } from '../model/upload-file.model';
import { MoveDocument } from '../model/move-document.model';

@Injectable({
  providedIn: 'root',
})
export class DocumentsService {
  private get tenantId() {
    return this.applicationStateService.tenantId;
  }

  constructor(
    private http: HttpClient,
    private applicationStateService: ApplicationStateService,
    @Inject(API_URL) private apiUrl: string
  ) {}

  setDocuments(
    documents: UploadDocument[],
    authorizationByProductId: string
  ): Observable<UploadDocument[]> {
    return this.http.post<UploadDocument[]>(
      this.getBaseUrl(authorizationByProductId),
      documents
    );
  }

  deleteDocument(
    documentFileName: string,
    authorizationByProductId: string
  ): void {
    this.getFilesMetaData(authorizationByProductId)
    .pipe(switchMap(res => {
      const documentId: string = res.find(elem => elem.documentFileName === documentFileName)?.docMetaDataId;

      if (documentId) {
         return this.http.delete<UploadDocument[]>(
          `${this.getBaseUrl(authorizationByProductId)}/?documentId=${documentId}`
        );
      }
      else {
        return of();
      }
    }))
    .subscribe({
      next: res => res,
      error: err => {
        console.error(err);
      }
    });
  }

  // Duplicate of deleteDocument(), but with return type as promise instead of void. Will need to clean this up in the future.
  deleteDocumentForMoveDocument(
    documentFileName: string,
    authorizationByProductId: string
  ): Observable<UploadDocument[]> {
    return this.getFilesMetaData(authorizationByProductId)
    .pipe(switchMap(res => {
      const documentId: string = res.find(elem => elem.documentFileName === documentFileName)?.docMetaDataId;

      if (documentId) {
         return this.http.delete<UploadDocument[]>(
          `${this.getBaseUrl(authorizationByProductId)}/?documentId=${documentId}`
        );
      }
      else {
        return of();
      }
    }));
  }

  getDocumentsForAuthorization(
    authorizationByProductId: string
  ): Observable<UploadDocument[]> {
    return this.http
      .get<IDocumentResponseDTO[]>(
        `${this.getBaseUrl(authorizationByProductId)}/GetFilesMetaData`
      )
      .pipe(
        map((documents) => {
          return documents.reduce((res, document) => {
            const idOfExistDoc = res.findIndex(
              (doc) =>
                doc.documentStorageLocation === document.documentStorageLocation
            );
            if (idOfExistDoc >= 0) {
              res[idOfExistDoc].documentTypes = [
                ...res[idOfExistDoc].documentTypes,
                document.documentType,
              ];
            } else {
              const {
                documentFileName,
                documentType,
                documentSource,
                dateTimeFaxReceived,
                documentDescription,
                documentStorageLocation,
                fileMetaData,
                docMetaDataId
              } = document;
              res.push({
                documentFileName,
                fileContent: null,
                documentTypes: [documentType],
                documentSource,
                dateTimeFaxReceived,
                description: documentDescription,
                documentStorageLocation,
                fileMetaData: {
                  size: fileMetaData.size,
                  type: fileMetaData.type,
                },
                docMetaDataId,
              });
            }
            return res;
          }, []);
        })
      );
  }

  getFilesMetaData(authorizationByProductId: string): Observable<IDocumentResponseDTO[]> {
    return this.http.get<IDocumentResponseDTO[]>(`${this.getBaseUrl(authorizationByProductId)}/GetFilesMetaData`);
  }

  getFileById(authorizationByProductId: string, id: string): Observable<DocumentExt> {
    return this.http.get<DocumentExt>(`${this.getBaseUrl(authorizationByProductId)}/GetDocumentById/${id}`);
  }

  /**
   * Method to Check/Unckeck documents.
   * @param authorizationByProductId Authorization Id.
   * @param docMetadataId Document Metadata Id.
   * @param isUncheck Bool override for unchecking document, defaults to false.
   * @returns Observable<IDocumentResponseDTO>
   */
  updateDocumentCheck(authorizationByProductId: string, docMetadataId: string, isUncheck = false) {
    if (isUncheck) {
      return this.http.put<IDocumentResponseDTO>(
        `${this.getBaseUrl(authorizationByProductId)}/UncheckDocument/${docMetadataId}`, null);
    }
    return this.http.put<IDocumentResponseDTO>(
      `${this.getBaseUrl(authorizationByProductId)}/CheckDocument/${docMetadataId}`, null);
  }

  uploadFile(authorizationByProductId: string, data: UploadFile[], isWidgetUpload = false): Observable<UploadFile[]> {
    // Add safe, URL encoded search parameter if there is a search term
    const options = isWidgetUpload ? { params: new HttpParams().set('isWidgetUpload', isWidgetUpload) } : {};

    return this.http.post<UploadFile[]>(`${this.getBaseUrl(authorizationByProductId)}`, data, options);
  }

  moveFiles(authorizationByProductId: string, data: MoveDocument[]): Observable<boolean> {
    return this.http.post<boolean>(`${this.getBaseUrl(authorizationByProductId)}/MoveDocuments`, data);
  }

  previewFileFaxDocs(file: DocumentExt): void {
    const blob = this.b64toBlob(
      file.fileContent,
      this.getContentType(file.fileType.toLowerCase())
    );
    const url = URL.createObjectURL(blob);
    const w = window.open(url, file.fileName);

    if (w) {
      w.addEventListener('beforeunload', () => {
        URL.revokeObjectURL(url);
      });
    }
  }

  private getBaseUrl(id: string): string {
    return `${this.apiUrl}/api/tenants/${this.tenantId}/authorizations/${id}/Documents`;
  }

  private b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
  }

  private getContentType(fileType: string): string {
    const imageTypes = ['png', 'jpg', 'jpeg', 'gif'];

    if (imageTypes.includes(fileType)) {
      return `image/${fileType}`;
    } else {
      return `application/${fileType}`;
    }
  }

  formatDocumentTypes(data: IDocumentResponseDTO[]) : IDocumentResponseDTO[]{
    const combinedUploadedDocs: IDocumentResponseDTO[] = [];
    // Split on our sources, due to display need for DocumentType (see next comment)
    const uploadedDocs = data.filter(doc => doc.documentSource == 'Upload');
    const otherDocs = data.filter(doc => doc.documentSource != 'Upload');

    const filteredFilesByName = otherDocs.filter((v, i) => otherDocs.findIndex(item => item.documentFileName === v.documentFileName) === i);

    // This forEach combines the DocumentTypes for the same upload. Each type is saved seperately, but needs to be recombined for Display.
    uploadedDocs.forEach(doc => {
      if (combinedUploadedDocs.some(d => d.documentFileName.toLowerCase() === doc.documentFileName.toLowerCase())) {
        return;
      }

      const tempFileArray = uploadedDocs.filter(d => d.documentFileName.toLowerCase() === doc.documentFileName.toLowerCase());
      const tmpDoc = { ...tempFileArray[0] };
      tmpDoc.documentType = tempFileArray.map(({documentType}) => documentType).join(', ');
      combinedUploadedDocs.push(tmpDoc);
    });

    const combinedDocs = [...filteredFilesByName, ...combinedUploadedDocs]

    // Sorting applied here due to the split we performed above
    return combinedDocs.sort((a,b) => new Date(b.dateTimeFaxReceived ?? b.createdOn).getTime() - new Date(a.dateTimeFaxReceived ?? a.createdOn).getTime());
  }
}